import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import {
	PaymentElement,
	useElements,
	useStripe,
} from '@stripe/react-stripe-js';
import { type StripeErrorType } from '@stripe/stripe-js';

import { type ISetPaymentMethodParams } from '@mopla/business-logic';
import { useLogger } from '@mopla/logger';
import {
	ArrowRightIcon,
	IconMessage,
	InfoBaseIcon,
	MplButton,
	MplErrorModal,
	MplTopSectionHeader,
	PageLoadingIndicator,
	StyledSafeAreaHeader,
} from '@mopla/ui';
import { getIpAddress } from '@mopla/utils';

import { useStripeRedirectUrl } from '../../utils';
import {
	type IStripePaymentContainerProps,
	StripePaymentContainer,
} from '../StripePaymentContainer';

import { LoaderWrapper, Root, ScrollWrapper } from './StripePayment.styles';

/** Bad form input or a declined card — expected outcomes, not something to alert on. */
const USER_ERROR_TYPES: StripeErrorType[] = ['validation_error', 'card_error'];

interface IStripePaymentElementProps {
	/** This callback is called when the Stripe Card PM is added successfully, meaning that we received a new paymentMethodId from Stripe. */
	onCardFlowCompleted: (payload: ISetPaymentMethodParams) => void;
	onClose: VoidFunction;
	_error?: string;
}

interface WrappedProps
	extends IStripePaymentContainerProps,
		IStripePaymentElementProps {}

export const StripePayment: FC<WrappedProps> = ({
	stripeClientSecret,
	stripeElementsOptions,
	...props
}) => {
	if (!stripeClientSecret) {
		return (
			<LoaderWrapper>
				<PageLoadingIndicator />
			</LoaderWrapper>
		);
	}

	return (
		<StripePaymentContainer stripeClientSecret={stripeClientSecret}>
			<StripePaymentElement {...props} />
		</StripePaymentContainer>
	);
};

const StripePaymentElement: FC<IStripePaymentElementProps> = (props) => {
	const logger = useLogger('payment');
	const { onCardFlowCompleted, onClose, _error } = props;
	const stripeRedirectUrl = useStripeRedirectUrl();
	const stripe = useStripe();
	const elements = useElements();
	const { t } = useTranslation(['payment']);
	const [error, setError] = useState<string | undefined>();
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		setError(_error);
	}, [_error]);

	const initiatePayment = useCallback(async () => {
		try {
			if (stripe && elements) {
				setIsSaving(true);

				const ip = await getIpAddress();

				/**
				 * When adding a redirect-based PM, the user leaves the current page context here, after calling this function.
				 * Logically, code execution here stops (without waiting for the promise).
				 * The promise resolves ONLY when adding a Card PM.
				 * */
				const { error, setupIntent } = await stripe.confirmSetup({
					elements,
					confirmParams: {
						return_url: stripeRedirectUrl,
						mandate_data: {
							customer_acceptance: {
								type: 'online',
								online: {
									user_agent: navigator.userAgent,
									ip_address: ip,
								},
							},
						},
					},
					// Uncomment below if you only want redirect for redirect-based payments
					redirect: 'if_required',
				});

				setIsSaving(false);

				if (error) {
					const isUserError = USER_ERROR_TYPES.includes(error.type);
					const message = `[StripePaymentElement] confirmSetup failure: ${error.message}`;
					const meta = {
						type: error.type,
						code: error.code,
						declineCode: error.decline_code,
						param: error.param,
					};

					if (isUserError) {
						logger.warn(message, meta);
					} else {
						logger.error(message, undefined, meta);
					}

					if (error.type === 'validation_error') {
						return;
					} else if (error.type === 'card_error') {
						setError(error.message);
					} else {
						setError('generic');
					}
				} else if (
					setupIntent?.payment_method &&
					typeof setupIntent.payment_method === 'string'
				) {
					logger.info('[StripePaymentElement] confirmSetup result', {
						hasPaymentMethodId: Boolean(setupIntent?.payment_method),
					});

					onCardFlowCompleted({
						paymentMethodId: setupIntent.payment_method,
					});
				} else {
					logger.error(
						'neither error nor payment method was returned from stripe'
					);
				}
			}
		} catch (err) {
			logger.error('Unexpected Payment flow error', err);
		}
	}, [elements, onCardFlowCompleted, stripe, stripeRedirectUrl, logger]);

	return (
		<>
			{error && (
				<MplErrorModal
					open
					title={t('error.title')}
					content={
						<Stack>
							<Typography variant="body1">{t('error.generic')}</Typography>
							<IconMessage
								icon={<InfoBaseIcon />}
								message={
									error === 'generic' ? t('error.generic_subtitle') : error
								}
							/>
						</Stack>
					}
					buttons={
						<MplButton
							testId="StripePaymentElement-ErrorModal-CloseButton"
							fullWidth
							onClick={onClose}
							endIcon={<ArrowRightIcon />}
						>
							{t('delete_modal.button.close')}
						</MplButton>
					}
					testId="StripePayment-MplErrorModal"
				/>
			)}
			<div
				style={{
					position: 'absolute',
					top: 0,
					left: 0,
					width: '100%',
					height: '100%',
					zIndex: 1200,
				}}
				id="payment-wrapper"
			>
				<Root hidden={false}>
					<StyledSafeAreaHeader>
						<MplTopSectionHeader
							title={t('header.select_stripe_method')}
							onBack={onClose}
						/>
					</StyledSafeAreaHeader>
					<ScrollWrapper>
						<Box padding="1rem">
							<PaymentElement
								options={{
									paymentMethodOrder: ['paypal', 'pay_by_bank', 'card'],
								}}
							/>
						</Box>
					</ScrollWrapper>
					<Box padding="16px">
						<MplButton
							fullWidth
							onClick={initiatePayment}
							disabled={isSaving}
							data-testid="booking-nextStep-button"
						>
							{isSaving ? t('button.is_saving') : t('button.save')}
						</MplButton>
					</Box>
				</Root>
			</div>
		</>
	);
};
